import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import CenterLoader from "../components/CenterLoader";
import NavBar from "../components/NavBar";
import BottomSection from "../components/BottomSection";

const isMobileDevice = () => {
  if (typeof window === "undefined" || !window.navigator) return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const isAndroidDevice = () => {
  if (typeof window === "undefined" || !window.navigator) return false;
  return /Android/i.test(navigator.userAgent);
};

const VerifyEmail = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const type = queryParams.get("type"); // Optional param to switch between staging/prod

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("");
  const [userData, setUserData] = useState<any>(null);
  const [deepLinkUrl, setDeepLinkUrl] = useState<string>("");
  const verificationStarted = useRef(false);

  const isMobile = isMobileDevice();
  const isAndroid = isAndroidDevice();

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token.");
      return;
    }

    const verifyUserEmail = async () => {
      if (verificationStarted.current) {
        return;
      }

      verificationStarted.current = true;

      try {
        const baseUrl =
          type === "prod"
            ? "https://homeowner-backend.azurewebsites.net/api"
            : "https://homeowner-staging.azurewebsites.net/api";

        const response = await axios.get(
          `${baseUrl}/user-verify-email?token=${token}`,
        );

        if (response.status === 200 && response.data) {
          setStatus("success");
          setUserData(response.data);

          // 1. Establish web session so web login works seamlessly
          if (response.data.jwtToken) {
            const session = {
              token: response.data.jwtToken,
              email: response.data.email || "",
              fullName: response.data.fullName || response.data.email || "",
            };
            try {
              localStorage.setItem("dv_session", JSON.stringify(session));
            } catch (e) {
              console.warn("Could not save session to localStorage", e);
            }
          }

          // 2. Prepare mobile app deep links
          const userString = encodeURIComponent(JSON.stringify(response.data));
          const customScheme = `homeowner://verify-success?user=${userString}`;
          const androidIntent = `intent://verify-success?user=${userString}#Intent;scheme=homeowner;package=com.arptix.homeowner;end`;
          const targetUrl = isAndroid ? androidIntent : customScheme;
          setDeepLinkUrl(targetUrl);

          if (isMobile) {
            setMessage(
              "Email verification successful! Redirecting you to the HomeOwner app...",
            );
            // Attempt immediate redirect on mobile
            try {
              window.location.href = targetUrl;
            } catch (err) {
              console.warn("Auto-redirect failed:", err);
            }
          } else {
            setMessage(
              "Email verification successful! Your account is verified and ready.",
            );
          }
        } else {
          setStatus("error");
          setMessage(
            "Verification failed. Please try again or contact support.",
          );
        }
      } catch (error: any) {
        console.error("Verification error:", error);
        setStatus("error");
        const errorMsg =
          error.response?.data?.message ||
          (typeof error.response?.data === "string"
            ? error.response?.data
            : "") ||
          error.message ||
          "The verification link may have expired or is no longer valid. Please go back to the app, tap Resend Email, and verify using the new email.";
        setMessage(errorMsg);
      }
    };

    verifyUserEmail();
  }, [token, type, isAndroid, isMobile]);

  return (
    <div className='flex flex-col min-h-screen'>
      <NavBar />
      <div className='flex-grow flex items-center justify-center py-20 px-4 bg-gray-50'>
        <div className='max-w-md w-full bg-white p-10 rounded-xl shadow-lg text-center'>
          {status === "loading" ? (
            <div className='py-10'>
              <CenterLoader />
              <p className='mt-4 text-gray-600'>Verifying your email...</p>
            </div>
          ) : status === "success" ? (
            <>
              <div className='mb-6 inline-flex items-center justify-center w-16 h-16 bg-green-100 text-green-600 rounded-full'>
                <svg
                  className='w-8 h-8'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'></path>
                </svg>
              </div>
              <h2 className='text-3xl font-bold text-gray-900 mb-4'>
                Success!
              </h2>
              <p className='text-gray-600 mb-8'>{message}</p>
              <div className='flex flex-col gap-3'>
                {deepLinkUrl ? (
                  <a
                    href={deepLinkUrl}
                    className='inline-block bg-[#F3943F] text-white font-semibold py-3 px-6 rounded-lg transition duration-300 hover:bg-[#e08432] shadow-md text-center'>
                    Open HomeOwner App
                  </a>
                ) : null}

                <Link
                  to='/document-verification'
                  className='inline-block bg-white text-[#F3943F] border border-[#F3943F] font-semibold py-3 px-6 rounded-lg transition duration-300 hover:bg-orange-50 text-center'>
                  Continue on Web (Document Verification)
                </Link>

                <Link
                  to='/'
                  className='text-gray-500 font-medium hover:underline text-sm mt-2'>
                  Go to Home Page
                </Link>

                {isMobile && (
                  <p className='text-xs text-gray-500 italic mt-2'>
                    If the app doesn't open automatically, tap the "Open HomeOwner App" button above.
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className='mb-6 inline-flex items-center justify-center w-16 h-16 bg-red-100 text-red-600 rounded-full'>
                <svg
                  className='w-8 h-8'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M6 18L18 6M6 6l12 12'></path>
                </svg>
              </div>
              <h2 className='text-3xl font-bold text-gray-900 mb-4'>Oops!</h2>
              <p className='text-gray-600 mb-8'>{message}</p>
              <Link
                to='/'
                className='inline-block bg-[#F3943F] text-white font-semibold py-3 px-6 rounded-lg transition duration-300 hover:bg-[#e08432]'>
                Go to Home
              </Link>
            </>
          )}
        </div>
      </div>
      <BottomSection />
    </div>
  );
};

export default VerifyEmail;
